import React, { useEffect, useState } from "react";
import { X, Pencil } from "lucide-react";
import toast from "react-hot-toast";
import { updateLead } from "../../../api/lead";

/**
 * Fix a lead's details by hand.
 *
 * Christian Gravias, Monday 12497182031, 22 Aug 2026: "Please make leads info
 * editable for admin. I want to be able to write something if it doesnt feed
 * through properly."
 *
 * That is a real, current gap rather than a nice-to-have. Meta's lead forms only
 * ask a few questions, and two of the live Collings forms ("Genneva Appraisal
 * Form", "Tomahawk Appraisal Form") do not map a name at all, so those leads land
 * in the CRM with the email address standing in for the person's name and no
 * property address anywhere. Until the Facebook forms are rebuilt, somebody has
 * to be able to type in what the feed did not send.
 *
 * Admin only, exactly as asked. Everything here goes through the existing
 * LeadDetailView PUT (partial=True), so there is no new endpoint and no
 * migration: name / phone / email / property_address are already writable fields
 * on the Lead.
 *
 * The Lead's own `name` is the only name field written. `full_name` on the model
 * is deprecated and the serializer already answers with `name` under both keys,
 * so writing one keeps the two in step.
 */
const LeadEditModal = ({ isOpen, lead, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", property_address: "" });
  const [saving, setSaving] = useState(false);

  // Seeded per lead, not per render: re-seeding on every parent refresh would
  // wipe a half-typed correction out from under whoever is fixing the row.
  useEffect(() => {
    if (!isOpen || !lead) return;
    setForm({
      name: lead.name || lead.full_name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      property_address: lead.property_address || lead.owner_subject_address || "",
    });
  }, [isOpen, lead]);

  if (!isOpen || !lead) return null;

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    const address = form.property_address.trim();

    if (!phone && !email) {
      toast.error("Leave a phone number or an email, or nobody can contact this lead.");
      return;
    }

    const payload = {
      name,
      phone,
      email,
      property_address: address,
      // Written together so the address the admin typed survives whichever of
      // the two fields the rest of the CRM reads. The Meta / REA intake fills
      // owner_subject_address with what the enquirer said and property_address
      // with the matched listing, and the Seller Leads row, the Add Appraisal
      // prefill and the agent's email each fall back differently between them.
      owner_subject_address: address,
    };

    setSaving(true);
    try {
      await updateLead(lead.id, payload);
      toast.success("Lead updated.");
      onSaved && onSaved({ ...lead, ...payload, full_name: name });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update the lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    // No close on a background click: this panel is full of typed-in text, and a
    // stray click outside the card used to throw it away with no warning
    // (Christian Gravias, 24 Aug 2026). Closing is the X, or Cancel.
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6 flex flex-col max-h-[90vh]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
            <Pencil className="w-4 h-4 text-[#55C5D0]" />
            Edit Lead Details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close edit lead"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <p className="text-xs text-gray-500">
            Fill in anything the feed did not send. Meta only passes on the answers its
            form actually asks for, so a name or an address can arrive blank.
          </p>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="lead-edit-name">
              Name
            </label>
            <input
              id="lead-edit-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="Who the lead is"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="lead-edit-phone">
                Phone
              </label>
              <input
                id="lead-edit-phone"
                type="text"
                value={form.phone}
                onChange={set("phone")}
                placeholder="0412 345 678"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="lead-edit-email">
                Email
              </label>
              <input
                id="lead-edit-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="name@example.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="lead-edit-address">
              Property address
            </label>
            <textarea
              id="lead-edit-address"
              value={form.property_address}
              onChange={set("property_address")}
              rows={2}
              placeholder="12 Smith Street, Preston VIC 3072"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Carried into Add as Appraisal and into the email the agent gets.
            </p>
          </div>

          {/* What the person actually sent through, so whoever is correcting the
              row can read the original before overwriting anything. Read-only:
              it is the enquirer's words, not the agency's. */}
          {(lead.initial_notes || "").trim() && (
            <div>
              <span className="block text-xs font-semibold text-gray-500 mb-1">What they sent through</span>
              <p className="text-xs text-gray-600 whitespace-pre-wrap border-l-2 border-[#55C5D0] bg-gray-50 rounded-r-lg px-3 py-2 max-h-40 overflow-y-auto">
                {lead.initial_notes}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadEditModal;
